import { BaseApi } from './base'
import StudyingSubjectsApi from './studying-subjects'

class TestManagementApi extends BaseApi {
    constructor() {
        super('admin/tests')
    }

    // Test Packs Management
    /**
     * Get all test packs with optional filtering
     * @param {Object} params - Query parameters { subject_id, status, difficulty, search, page }
     * @returns {Promise}
     */
    getTestPacks(params = {}) {
        return this.get('/test-packs', { params })
    }

    /**
     * Create a new test pack
     * @param {Object|FormData} data - Test pack data
     * @returns {Promise}
     */
    createTestPack(data) {
        const config = {
            headers: {
                'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
            }
        }
        return this.post('/test-packs', data, config)
    }

    /**
     * Update an existing test pack
     * @param {Number} id - Test pack ID
     * @param {Object|FormData} data - Updated test pack data
     * @returns {Promise}
     */
    updateTestPack(id, data) {
        const config = {
            headers: {
                'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json',
            }
        }
        return this.update(`/test-packs/${id}`, data)
    }

    /**
     * Delete a test pack (archives it)
     * @param {Number} id - Test pack ID
     * @returns {Promise}
     */
    deleteTestPack(id) {
        return this.delete(`/test-packs/${id}`)
    }

    /**
     * Permanently delete a test pack
     * @param {Number} id - Test pack ID
     * @returns {Promise}
     */
    forceDeleteTestPack(id) {
        return this.delete(`/test-packs/${id}/force`)
    }

    /**
     * Duplicate a test pack
     * @param {Number} id - Test pack ID to duplicate
     * @returns {Promise}
     */
    duplicateTestPack(id) {
        return this.post(`/test-packs/${id}/duplicate`)
    }

    /**
     * Export test pack questions to JSON
     * @param {Number} id - Test pack ID
     * @returns {Promise}
     */
    exportQuestions(id) {
        return this.get(`/test-packs/${id}/export`)
    }

    // Questions Management
    /**
     * Get all questions for a test pack
     * @param {Number} testPackId - Test pack ID
     * @returns {Promise}
     */
    getTestPackQuestions(testPackId) {
        return this.get(`/test-packs/${testPackId}/questions`)
    }

    /**
     * Create a new question
     * @param {Object|FormData} data - Question data
     * @returns {Promise}
     */
    createQuestion(data) {
        return this.post(`/questions`, data)
    }

    /**
     * Update an existing question
     * @param {Number} id - Question ID
     * @param {Object|FormData} data - Updated question data
     * @returns {Promise}
     */
    updateQuestion(id, data) {
        return this.update(`/questions/${id}`, data)
    }

    /**
     * Delete a question
     * @param {Number} id - Question ID
     * @returns {Promise}
     */
    deleteQuestion(id) {
        return this.delete(`/questions/${id}`)
    }

    /**
     * Import questions from file
     * @param {FormData} data - Import data with file and test_pack_id
     * @returns {Promise}
     */
    importQuestions(data) {
        return this.post(`/questions/import`, data)
    }

    // Analytics and Statistics
    /**
     * Get test analytics and statistics
     * @returns {Promise}
     */
    getAnalytics() {
        return this.get(`/analytics`)
    }

    // Helper Methods
    /**
     * Get subjects for dropdown (reuse from existing API)
     * @returns {Promise}
     */
    getSubjects() {
        // Use the existing subjects API
        return StudyingSubjectsApi.getSubjects()
    }

    /**
     * Create test pack with comprehensive validation
     * @param {Object} testPackData - Test pack form data
     * @returns {Promise}
     */
    async createTestPackWithValidation(testPackData) {
        // Validate required fields
        const requiredFields = ['name', 'subject_id', 'difficulty_level']
        const missingFields = requiredFields.filter(field => !testPackData[field])

        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`)
        }

        // Validate difficulty level
        const validDifficulties = ['beginner', 'intermediate', 'advanced']
        if (!validDifficulties.includes(testPackData.difficulty_level)) {
            throw new Error('Invalid difficulty level')
        }

        return this.createTestPack(testPackData)
    }

    /**
     * Create question with comprehensive validation
     * @param {Object} questionData - Question form data
     * @returns {Promise}
     */
    async createQuestionWithValidation(questionData) {
        // Validate required fields
        const requiredFields = ['test_pack_id', 'question', 'question_type', 'points']
        const missingFields = requiredFields.filter(field => !questionData[field])

        if (missingFields.length > 0) {
            throw new Error(`Missing required fields: ${missingFields.join(', ')}`)
        }

        // Validate question type
        const validTypes = ['multiple_choice', 'true_false', 'essay', 'short_answer', 'fill_blank']
        if (!validTypes.includes(questionData.question_type)) {
            throw new Error('Invalid question type')
        }

        // Validate multiple choice specific data
        if (questionData.question_type === 'multiple_choice') {
            if (!questionData.options || !Array.isArray(questionData.options) || questionData.options.length < 2) {
                throw new Error('Multiple choice questions must have at least 2 options')
            }
            if (!questionData.correct_answer) {
                throw new Error('Multiple choice questions must have a correct answer')
            }
        }

        // Validate true/false specific data
        if (questionData.question_type === 'true_false') {
            if (!questionData.correct_answer || !['true', 'false'].includes(questionData.correct_answer.toLowerCase())) {
                throw new Error('True/False questions must have correct_answer as "true" or "false"')
            }
        }

        // Validate essay specific data
        if (questionData.question_type === 'essay') {
            if (!questionData.essay_prompt) {
                throw new Error('Essay questions must have an essay prompt')
            }
        }

        return this.createQuestion(questionData)
    }

    /**
     * Bulk operations
     * @param {Array} testPackIds - Array of test pack IDs
     * @param {String} operation - Operation to perform (delete, duplicate, activate, deactivate)
     * @returns {Promise}
     */
    async bulkTestPackOperation(testPackIds, operation) {
        const promises = testPackIds.map(id => {
            switch (operation) {
                case 'delete':
                    return this.deleteTestPack(id)
                case 'duplicate':
                    return this.duplicateTestPack(id)
                case 'activate':
                    return this.updateTestPack(id, { status: 'active' })
                case 'deactivate':
                    return this.updateTestPack(id, { status: 'inactive' })
                default:
                    return Promise.reject(new Error(`Unknown operation: ${operation}`))
            }
        })

        const results = await Promise.allSettled(promises)

        const successful = results.filter(result => result.status === 'fulfilled').length
        const failed = results.filter(result => result.status === 'rejected').length

        return {
            successful,
            failed,
            results
        }
    }

    /**
     * Search test packs with advanced filters
     * @param {Object} filters - Advanced search filters
     * @returns {Promise}
     */
    searchTestPacks(filters) {
        const params = {}

        if (filters.search) {
            params.search = filters.search
        }
        if (filters.subject_id) {
            params.subject_id = filters.subject_id
        }
        if (filters.difficulty) {
            params.difficulty = filters.difficulty
        }
        if (filters.status) {
            params.status = filters.status
        }
        if (filters.created_from) {
            params.created_from = filters.created_from
        }
        if (filters.created_to) {
            params.created_to = filters.created_to
        }
        if (filters.min_questions) {
            params.min_questions = filters.min_questions
        }
        if (filters.max_questions) {
            params.max_questions = filters.max_questions
        }

        return this.getTestPacks(params)
    }

    /**
     * Get test pack statistics
     * @param {Number} id - Test pack ID
     * @returns {Promise}
     */
    async getTestPackStats(id) {
        try {
            const response = await this.getTestPackQuestions(id)
            const testPack = response.data.test_pack
            const questions = response.data.questions

            const stats = {
                total_questions: questions.length,
                total_points: questions.reduce((sum, q) => sum + (q.points || 0), 0),
                question_types: {},
                difficulty_distribution: {},
                average_points_per_question: 0,
                has_media: questions.some(q => q.question_image || q.question_audio),
                estimated_time: testPack.time || questions.length * 2 // Estimate 2 minutes per question
            }

            // Count question types
            questions.forEach(question => {
                stats.question_types[question.question_type] =
                    (stats.question_types[question.question_type] || 0) + 1
            })

            // Count difficulty distribution
            questions.forEach(question => {
                const difficulty = question.difficulty_level || 'medium'
                stats.difficulty_distribution[difficulty] =
                    (stats.difficulty_distribution[difficulty] || 0) + 1
            })

            // Calculate average points
            if (questions.length > 0) {
                stats.average_points_per_question = stats.total_points / questions.length
            }

            return { ...response.data, stats }

        } catch (error) {
            console.error('Error getting test pack stats:', error)
            throw error
        }
    }

    /**
     * Validate import file format
     * @param {File} file - Import file
     * @param {String} format - Expected format ('json' or 'csv')
     * @returns {Promise}
     */
    async validateImportFile(file, format) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()

            reader.onload = (e) => {
                try {
                    const content = e.target.result
                    let data = []

                    if (format === 'json') {
                        data = JSON.parse(content)
                        if (!Array.isArray(data)) {
                            throw new Error('JSON file must contain an array of questions')
                        }
                    } else if (format === 'csv') {
                        const lines = content.split('\n').filter(line => line.trim())
                        if (lines.length < 2) {
                            throw new Error('CSV file must have at least a header row and one data row')
                        }
                        const headers = lines[0].split(',')
                        if (!headers.includes('question') || !headers.includes('question_type')) {
                            throw new Error('CSV file must have "question" and "question_type" columns')
                        }
                    }

                    resolve({
                        valid: true,
                        questionCount: format === 'json' ? data.length : lines.length - 1,
                        preview: format === 'json' ? data.slice(0, 3) : lines.slice(0, 4)
                    })

                } catch (error) {
                    reject({
                        valid: false,
                        error: error.message
                    })
                }
            }

            reader.onerror = () => {
                reject({
                    valid: false,
                    error: 'Failed to read file'
                })
            }

            reader.readAsText(file)
        })
    }
}

export default TestManagementApi